import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { mitHartenUmbruechen } from "./markdown";

/**
 * Der Text einer Neuigkeit, als Markdown gesetzt (Ueberschriften, Hervorhebungen, Listen,
 * Links, Trennlinien).
 *
 * Bewusst nicht erlaubt: Bilder, Tabellen und Code. Ein Bild hat die Neuigkeit als eigenes
 * Feld, in dem es auf 16:9 beschnitten wird; eine Tabelle passt nicht in eine 410 Pixel
 * schmale Spalte. Was trotzdem jemand schreibt, verschwindet nicht - es steht als Text da
 * (`unwrapDisallowed`).
 *
 * Rohes HTML rendert `react-markdown` von sich aus nicht, und wir holen es auch nicht per
 * `rehype-raw` zurueck: Der Text wird zwar nur bei uns geschrieben, aber nichts an dieser
 * Stelle zwingt dazu, ihm zu vertrauen.
 *
 * `react-markdown` und seine Abhaengigkeiten liefern nur ESM aus, und das Jest von
 * create-react-app uebersetzt node_modules nicht. Deshalb steht in der package.json eine
 * `jest.transformIgnorePatterns`-Ausnahme mit genau diesen Paketen. Faellt ein Test hier
 * nach einem Update mit "Unexpected token 'export'" aus, fehlt dort ein Paketname - der
 * steht in der Fehlermeldung.
 */

const klassen = {
  ueberschrift: "mt-3 text-base font-semibold leading-snug text-gray-900 first:mt-0",
  kleineUeberschrift: "mt-3 text-sm font-semibold leading-snug text-gray-900 first:mt-0",
};

export const NeuigkeitText = ({ text, ton = "dunkel" }: { text: string; ton?: "hell" | "dunkel" }) => (
  <div className={`text-sm leading-relaxed ${ton === "hell" ? "text-gray-500" : "text-gray-700"}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      disallowedElements={["img", "table", "thead", "tbody", "tr", "th", "td", "pre", "code"]}
      unwrapDisallowed
      components={{
        h1: ({ children }) => <h3 className={klassen.ueberschrift}>{children}</h3>,
        h2: ({ children }) => <h3 className={klassen.ueberschrift}>{children}</h3>,
        h3: ({ children }) => <h3 className={klassen.ueberschrift}>{children}</h3>,
        h4: ({ children }) => <h4 className={klassen.kleineUeberschrift}>{children}</h4>,
        h5: ({ children }) => <h4 className={klassen.kleineUeberschrift}>{children}</h4>,
        h6: ({ children }) => <h4 className={klassen.kleineUeberschrift}>{children}</h4>,
        p: ({ children }) => <p className="mt-2 first:mt-0">{children}</p>,
        ul: ({ children }) => <ul className="mt-2 list-disc space-y-1 pl-5 first:mt-0">{children}</ul>,
        ol: ({ children }) => <ol className="mt-2 list-decimal space-y-1 pl-5 first:mt-0">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        hr: () => <hr className="my-3 border-gray-200" />,
        blockquote: ({ children }) => (
          <blockquote className="mt-2 border-l-2 border-gray-200 pl-3 text-gray-600">{children}</blockquote>
        ),
        // Wie die Knoepfe unter der Neuigkeit: in einem neuen Reiter, damit der Kunde
        // seine Arbeit in kandidat.io nicht verliert.
        a: ({ children, href }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-neuigkeit underline underline-offset-2"
          >
            {children}
          </a>
        ),
      }}
    >
      {mitHartenUmbruechen(text)}
    </ReactMarkdown>
  </div>
);

export default NeuigkeitText;
